import React, { useContext, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { bool, func } from 'prop-types';
import { Dropdown } from '@tesla/informed-tds';
import { Button, ButtonGroup, FormLayout } from '@tesla/design-system-react';
import { useFieldApi, useFieldState, useFormApi } from 'informed';
import pick from 'lodash/pick';
import find from 'lodash/find';
import get from 'lodash/get';
import pickBy from 'lodash/pickBy';
import DebounceInput from '../DebounceInput';
import { DataContext, QueryContext } from '../../containers/Search/context';
import { objectToSelectOptions } from '../../utils/common';
import messages from './messages';

const Filters = ({ isGrohmannSite, resetFilters }) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const api = useContext(DataContext);
  const { setQuery } = useContext(QueryContext);
  const formApi = useFormApi();
  const { value: region = '' } = useFieldState('region');
  const { value: site = '' } = useFieldState('site');
  const { value: state = '' } = useFieldState('state');
  const siteFieldApi = useFieldApi('site');
  const locationFieldApi = useFieldApi('location');
  const stateFieldApi = useFieldState('state');

  const geo = api.getGeographical();

  // Category (Department) options
  const departments = api.getDepartments();
  const departmentOptions = Object.keys(departments)
    .map((value) => ({
      label: api.lookupDepartment(value),
      value,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Job Type options
  const typeLabelMapping = {
    fulltime: messages.TypeFullTime,
    parttime: messages.TypePartTime,
    intern: messages.TypeIntern,
    seasonal: messages.TypeSeasonal,
  };

  // This filters out options we dont have translations for:
  const types = pickBy(api.getTypes(), (value, key) => !!typeLabelMapping[api.lookupType(key)]);
  const typeOptions = Object.keys(types).map((value) => ({
    value,
    label: formatMessage(typeLabelMapping[api.lookupType(value)]),
  }));

  // Region options
  const allRegions = api.getRegions();
  const regions = [...objectToSelectOptions(allRegions)];
  const [geoStates, setGeoStates] = useState([]);
  const [sites, setSites] = useState([]); // countries
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    // This effect will be used to recalculate the country option list.

    let options;
    if (region && region !== '') {
      const allCountries = api.getCountries();
      const geoRegion = find(geo, { id: region });
      const countriesOfRegion = (geoRegion && geoRegion.sites) || []; // countries
      const countriesOfRegionIds = countriesOfRegion.map(({ id }) => id);

      // This will pick only the countries from the selected region.
      options = pick(allCountries, countriesOfRegionIds);
    } else {
      options = api.getCountries();
    }

    setSites([...objectToSelectOptions(options)]);
  }, [region]);

  useEffect(() => {
    // This effect will be used to determine the states if we select a country that has states.
    if (site === 'US') {
      const stateData = api.getStatesInCountry(site);
      if (!stateData) {
        return;
      }
      const stateOptions = stateData
        .filter(({ cities = {} }) => Object.keys(cities).length)
        .map(({ id, name }) => ({
          value: id,
          label: name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setGeoStates(stateOptions);
    }
  }, [site]);

  useEffect(() => {
    // This effect will be used to recalculate the location option list.
    let options = [];

    // We don't source the locations if we have selected 'US' because we
    // require a state to be selected as well, otherwise we get strange behaviour
    // that certain values will be duplicated in the list and fail to be removed upon
    // re-render of the list.
    if (site && site !== '' && (!api.countryHasStates(site) || !!state)) {
      options = api.getCityNamesInCountry(site, state);
    }

    const optionsMap = options
      .filter(Boolean)
      .sort()
      .map((value) => ({
        value,
        label: value,
      }));
    setLocations(optionsMap);
  }, [site, state]);

  const fireEvent = (interaction, label) => {
    window.dataLayer.push({
      event: 'careers',
      interaction,
      label,
    });
  };

  const handleChange = (e) => {
    const { target } = e.event;
    fireEvent('search', target.value);
  };

  const onDropdownChange = (dataLabel) => {
    fireEvent('search-filter', dataLabel);
  };

  return (
    <FormLayout style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <DebounceInput
        name="query"
        label={formatMessage(messages.Search)}
        onDelayedChange={setQuery}
        debounceTimeout={200}
        maxWait={1000}
        onBlur={handleChange}
      />
      <Dropdown
        name="department"
        label={formatMessage(messages.JobCategory)}
        options={departmentOptions}
        placeholder={formatMessage(messages.EmptyOption)}
        data-label="job-category"
        onChange={() => onDropdownChange('job-category')}
      />
      {!isGrohmannSite && (
        <>
          <Dropdown
            name="type"
            label={formatMessage(messages.TypeLabel)}
            placeholder={formatMessage(messages.EmptyOption)}
            options={typeOptions}
            data-label="job-type"
            onChange={() => onDropdownChange('job-type')}
          />
          <Dropdown
            name="region"
            label={formatMessage(messages.Region)}
            data-label="region"
            options={regions}
            placeholder={formatMessage(messages.EmptyOption)}
            onChange={() => {
              siteFieldApi.setValue('');
              if (stateFieldApi.setValue) {
                stateFieldApi.setValue('');
              }
              if (locationFieldApi.setValue) {
                locationFieldApi.setValue('');
              }
              onDropdownChange('region');
            }}
          />
          <Dropdown
            name="site" // country
            label={formatMessage(messages.Country)}
            data-label="site"
            options={sites}
            placeholder={formatMessage(messages.EmptyOption)}
            onChange={() => {
              if (stateFieldApi.setValue) {
                stateFieldApi.setValue('');
              }
              if (locationFieldApi.setValue) {
                locationFieldApi.setValue('');
              }
              onDropdownChange('site');
            }}
          />
        </>
      )}
      <Dropdown
        name="state"
        label={formatMessage(messages.States)}
        data-label="states"
        options={geoStates}
        placeholder={formatMessage(messages.EmptyOption)}
        onChange={() => {
          locationFieldApi.setValue('');
          onDropdownChange('states');
        }}
        relevant={({ formState: { values } }) => values.site && api.countryHasStates(values.site)}
      />
      <Dropdown
        name="location"
        label={formatMessage(messages.Location)}
        data-label="location"
        placeholder={formatMessage(messages.EmptyOption)}
        options={locations}
        relevant={({ formState: { values } }) =>
          (!!values.site && !api.countryHasStates(values.site)) || (!!values.site && !!values.state)
        }
        onChange={() => onDropdownChange('location')}
      />
      <ButtonGroup>
        <Button
          type="reset"
          variant="secondary"
          onClick={() => {
            formApi.clearValues();
            setQuery('');
            resetFilters();
          }}
        >
          {formatMessage(messages.Reset)}
        </Button>
      </ButtonGroup>
    </FormLayout>
  );
};

Filters.propTypes = {
  isGrohmannSite: bool,
  resetFilters: func.isRequired,
};

Filters.defaultProps = {
  isGrohmannSite: false,
};

const mapStateToProps = (state) => ({
  isGrohmannSite: get(state, 'app.isGrohmannSite', false),
});

export default connect(mapStateToProps)(Filters);
